import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MigrationDocumentType,
  MinimalOrgDto,
  useAdminGetFencyMigrationStatsQuery,
  useAdminMigrateFencyContractMetadataMutation,
  useAdminMigrateFencyDocumentsMutation,
  useAdminRestampFencyAccessMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { OrganizationSelector } from "../../../common/organization/OrganizationSelector";

const DEFAULT_LIMIT = 100;

type MigrationKind = "REGULAR" | "SIGNED" | "CONTRACT_METADATA" | "ACCESS";

export const FencyMigration = () => {
  useLoggedInWithOrgContextState();
  const toast = useToast();
  const { t } = useTranslation();
  const apiErrorDisplayer = useApiError();
  const [migrateDocuments, { isLoading: isMigratingDocuments }] = useAdminMigrateFencyDocumentsMutation();
  const [migrateContracts, { isLoading: isMigratingContracts }] = useAdminMigrateFencyContractMetadataMutation();
  const [restampAccess, { isLoading: isRestampingAccess }] = useAdminRestampFencyAccessMutation();
  const isLoading = isMigratingDocuments || isMigratingContracts || isRestampingAccess;

  const [selectedOrg, setSelectedOrg] = useState<MinimalOrgDto | undefined>();
  const [kind, setKind] = useState<MigrationKind>("REGULAR");
  const [allSyncedOrganizations, setAllSyncedOrganizations] = useState(false);
  const targetOrgId = selectedOrg?.id ?? "";
  const runsAcrossSyncedOrganizations = kind === "ACCESS" && allSyncedOrganizations;
  const hasTarget = runsAcrossSyncedOrganizations || targetOrgId !== "";

  const {
    data: stats,
    isFetching: isStatsFetching,
    isLoading: isStatsLoading,
    isError: isStatsError,
    refetch: refetchStats,
  } = useAdminGetFencyMigrationStatsQuery(
    runsAcrossSyncedOrganizations ? {} : { targetOrganizationId: targetOrgId },
    { skip: !hasTarget },
  );

  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [lastRun, setLastRun] = useState<{ successCount: number; failureCount: number } | null>(null);

  const runMigration = async () => {
    if (!hasTarget) {
      return;
    }

    const safeLimit = Number.isFinite(limit) && limit >= 1 ? Math.floor(limit) : DEFAULT_LIMIT;

    const result =
      kind === "ACCESS"
        ? await restampAccess({
            adminRestampFencyAccessRequest: runsAcrossSyncedOrganizations
              ? { limit: safeLimit }
              : { organizationId: targetOrgId, limit: safeLimit },
          })
        : kind === "CONTRACT_METADATA"
        ? await migrateContracts({
            adminMigrateFencyContractMetadataRequest: { organizationId: targetOrgId, limit: safeLimit },
          })
        : await migrateDocuments({
            adminMigrateFencyRequest: {
              organizationId: targetOrgId,
              documentType: kind === "SIGNED" ? MigrationDocumentType.Signed : MigrationDocumentType.Regular,
              limit: safeLimit,
            },
          });

    if ("data" in result) {
      setLastRun({
        successCount: result.data.successCount,
        failureCount: result.data.failureCount,
      });
      void refetchStats();
      toast({
        title: t("Fency migration finished"),
        status: "success",
        position: "top-right",
      });
    } else {
      apiErrorDisplayer.trigger(result.error);
    }
  };

  const refreshStats = async () => {
    const result = await refetchStats();
    if (result.error) {
      apiErrorDisplayer.trigger(result.error);
    }
  };

  return (
    <VStack align="stretch" spacing={6} pt={4}>
      <Heading as="h3" size="md">
        {t("Run migration")}
      </Heading>
      <Text color="smMuted" fontSize="sm">
        {t(
          "Select the target organization below, then migrate its documents and contracts to Fency. Start with a low batch limit to verify results.",
        )}
      </Text>

      <FormControl maxW="md">
        <FormLabel>{t("Content type")}</FormLabel>
        <Select
          value={kind}
          onChange={(e) => setKind(e.target.value as MigrationKind)}
          bg="smBackground"
          borderColor="smBorder"
        >
          <option value="REGULAR">{t("Regular documents")}</option>
          <option value="SIGNED">{t("Signed documents")}</option>
          <option value="CONTRACT_METADATA">{t("Contract metadata")}</option>
          <option value="ACCESS">{t("Access tokens on indexed memories")}</option>
        </Select>
      </FormControl>

      {kind === "ACCESS" && (
        <Checkbox
          isChecked={allSyncedOrganizations}
          onChange={(e) => setAllSyncedOrganizations(e.target.checked)}
          borderColor="smBorder"
        >
          {t("All organizations with Fency sync")}
        </Checkbox>
      )}

      {!runsAcrossSyncedOrganizations && (
        <FormControl maxW="xl">
          <FormLabel>{t("Organization")}</FormLabel>
          <OrganizationSelector
            size="md"
            organization={selectedOrg}
            onChange={({ organization }) => setSelectedOrg(organization)}
          />
        </FormControl>
      )}

      <FormControl maxW="xs">
        <FormLabel>{t("Batch limit")}</FormLabel>
        <NumberInput
          min={1}
          max={10000}
          value={limit}
          onChange={(_, valueAsNumber) => setLimit(Number.isNaN(valueAsNumber) ? DEFAULT_LIMIT : valueAsNumber)}
          bg="smBackground"
          borderColor="smBorder"
        >
          <NumberInputField />
        </NumberInput>
      </FormControl>

      <Box>
        <Button
          colorScheme="teal"
          onClick={runMigration}
          isLoading={isLoading}
          isDisabled={isLoading || !hasTarget}
        >
          {t("Run Fency migration")}
        </Button>
      </Box>

      {lastRun !== null && (
        <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p={4} bg="smBackgroundSecondary">
          <Text fontWeight="semibold" mb={3} color="smText">
            {t("Last batch result")}
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
            <Stat>
              <StatLabel>{t("Successful")}</StatLabel>
              <StatNumber color="smSuccess">{lastRun.successCount}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{t("Failed")}</StatLabel>
              <StatNumber color="smError">{lastRun.failureCount}</StatNumber>
            </Stat>
          </SimpleGrid>
        </Box>
      )}

      <Divider borderColor="smBorder" />

      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Heading as="h3" size="md">
          {t("Fency migration statistics")}
        </Heading>
        <Button
          variant="outline"
          borderColor="smBorder"
          onClick={refreshStats}
          isLoading={isStatsFetching}
          isDisabled={isStatsLoading || !hasTarget}
        >
          {t("Refresh statistics")}
        </Button>
      </HStack>

      {!hasTarget ? (
        <Text color="smMuted" fontSize="sm">
          {t("Select an organization above to load statistics for that organization.")}
        </Text>
      ) : isStatsError && stats === undefined ? (
        <VStack align="stretch" spacing={3}>
          <Text color="smError">{t("Could not load statistics")}</Text>
          <Button variant="outline" borderColor="smBorder" onClick={refreshStats} isLoading={isStatsFetching}>
            {t("Refresh statistics")}
          </Button>
        </VStack>
      ) : isStatsLoading && stats === undefined ? (
        <Spinner color="smPrimary" />
      ) : stats !== undefined ? (
        <VStack align="stretch" spacing={4}>
          <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p={4} bg="smBackgroundSecondary">
            <Text fontWeight="semibold" mb={3} color="smText">
              {t("Regular documents")}
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 4 }} spacing={4}>
              <Stat>
                <StatLabel>{t("Total documents")}</StatLabel>
                <StatNumber>{stats.regularDocuments.totalDocuments}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Missing Fency config")}</StatLabel>
                <StatNumber color="smWarning">{stats.regularDocuments.missingFencyConfigCount}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Sync errors")}</StatLabel>
                <StatNumber color="smError">{stats.regularDocuments.synchronizationErrorCount}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Missing access stamp")}</StatLabel>
                <StatNumber color="smWarning">{stats.regularDocuments.missingAccessCount}</StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>
          <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p={4} bg="smBackgroundSecondary">
            <Text fontWeight="semibold" mb={3} color="smText">
              {t("Signed documents")}
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 4 }} spacing={4}>
              <Stat>
                <StatLabel>{t("Total documents")}</StatLabel>
                <StatNumber>{stats.signedDocuments.totalDocuments}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Missing Fency config")}</StatLabel>
                <StatNumber color="smWarning">{stats.signedDocuments.missingFencyConfigCount}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Sync errors")}</StatLabel>
                <StatNumber color="smError">{stats.signedDocuments.synchronizationErrorCount}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Missing access stamp")}</StatLabel>
                <StatNumber color="smWarning">{stats.signedDocuments.missingAccessCount}</StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>
          <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p={4} bg="smBackgroundSecondary">
            <Text fontWeight="semibold" mb={3} color="smText">
              {t("Contract metadata")}
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 4 }} spacing={4}>
              <Stat>
                <StatLabel>{t("Total contracts")}</StatLabel>
                <StatNumber>{stats.contractMetadata.totalContracts}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Not indexed")}</StatLabel>
                <StatNumber color="smWarning">{stats.contractMetadata.missingMemoryCount}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Sync errors")}</StatLabel>
                <StatNumber color="smError">{stats.contractMetadata.synchronizationErrorCount}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t("Missing access stamp")}</StatLabel>
                <StatNumber color="smWarning">{stats.contractMetadata.missingAccessCount}</StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>
        </VStack>
      ) : null}
    </VStack>
  );
};
